// src/services/phonicsWorksheetService.ts
// Main service: Orchestrates mood-based phonics worksheet generation
import worksheetWordsData from '../data/worksheet_words.json'
import {
  selectWordsForMood,
  selectActivitiesForMood,
  getStylingForMood,
  generateParentNotes,
  calculateEstimatedTime,
  type MoodBasedConfig
} from './adapters/phonicsDataAdapter'
import type {
  WorksheetConfig,
  WorksheetData,
  Activity,
  ActivityType
} from '../types/phonicsWorksheet'

/**
 * Main function: Generate complete mood-based phonics worksheet
 * This is called from the UI component
 */
export async function generateMoodBasedWorksheet(
  config: WorksheetConfig
): Promise<WorksheetData> {
  try {
    // Validation
    if (!config.pattern || !config.mood) {
      throw new Error('Pattern and mood are required')
    }

    // 1. Select words based on pattern and mood
    const wordSelection = selectWordsForMood(config.pattern, config.mood)
    
    // 2. Select activities based on mood and skip options
    const activitySelection = selectActivitiesForMood(config.mood, config.skipOptions)
    
    // 3. Build full activity objects with templates and words
    const activities = buildActivityObjects(
      activitySelection.activityTypes,
      wordSelection.words
    )
    
    // 4. Get styling configuration for this mood
    const styling = getStylingForMood(config.mood)
    
    // 5. Generate parent notes
    const parentNotes = generateParentNotes(config, wordSelection, activitySelection)
    
    // 6. Calculate metadata
    const estimatedTime = calculateEstimatedTime(
      activitySelection.activityCount,
      wordSelection.wordCount,
      config.mood
    )

    return {
      config,
      words: wordSelection.words,
      activities,
      estimatedTime,
      wordCount: wordSelection.wordCount,
      activityCount: activities.length,
      styling,
      parentNotes
    }
  } catch (error) {
    console.error('Error generating mood-based worksheet:', error)
    throw new Error(`Failed to generate worksheet: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Build full activity objects with words and metadata
 */
function buildActivityObjects(
  activityTypes: string[],
  words: string[]
): Activity[] {
  const activityTemplates = (worksheetWordsData as any).activity_templates
  const activities: Activity[] = []

  activityTypes.forEach((activityType) => {
    const template = activityTemplates[activityType]
    
    if (!template) {
      console.warn(`Activity template not found: ${activityType}`)
      return
    }

    // Determine how many words this activity needs
    const wordsPerActivity = Math.min(
      template.words_per_activity || 4,
      words.length
    )

    // Select subset of words for this activity
    const activityWords = selectWordsForActivity(words, wordsPerActivity)

    activities.push({
      type: activityType as ActivityType,
      name: template.name,
      description: template.description,
      instructions: template.instructions,
      words: activityWords,
      cognitiveLoad: template.cognitive_load,
      motorDemand: template.motor_demand
    })
  })

  return activities
}

/**
 * Select words for a specific activity
 * Strategy: Rotate through the word list to ensure variety
 */
let wordRotationIndex = 0

function selectWordsForActivity(
  availableWords: string[],
  count: number
): string[] {
  const selectedWords: string[] = []
  
  for (let i = 0; i < count && i < availableWords.length; i++) {
    const index = (wordRotationIndex + i) % availableWords.length
    selectedWords.push(availableWords[index])
  }
  
  wordRotationIndex = (wordRotationIndex + count) % availableWords.length
  
  return selectedWords
}

/**
 * Reset word rotation (useful for testing or new worksheet generation)
 */
export function resetWordRotation() {
  wordRotationIndex = 0
}